import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Copy, Check, ArrowRight, Twitter, Linkedin, Mail, Sparkles } from 'lucide-react';
import { useReferral } from '../hooks/useReferral';

export function InvitePage() {
  const { stats, loading, referralLink, copyLink } = useReferral();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = 'Invite Friends | VZMO';
  }, []);

  const handleCopy = async () => {
    const success = await copyLink();
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareText = "I've been using VZMO for personal growth coaching and it's been amazing. Try it free for 37 days with my invite link:";

  const shareOnTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralLink || '')}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const shareOnLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink || '')}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const shareViaEmail = () => {
    const subject = "Try VZMO - Get 37 days free";
    const body = `Hey!\n\n${shareText}\n\n${referralLink}\n\nI think you'd really like it for setting goals and staying accountable. Let me know what you think!`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF8F4] to-white py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F4] to-white py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gold-400 to-gold-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Gift className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
            Give clarity, get clarity
          </h1>
          <p className="text-lg text-slate-600 max-w-md mx-auto">
            Share VZMO with friends and you both get 30 extra days added to your trial.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 mb-8">
          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="text-3xl font-bold text-gold-600 mb-1">{stats?.total_referrals || 0}</div>
              <div className="text-sm text-slate-500">Friends Invited</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="text-3xl font-bold text-sage-600 mb-1">{stats?.completed_referrals || 0}</div>
              <div className="text-sm text-slate-500">Signed Up</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="text-3xl font-bold text-coral-600 mb-1">{stats?.months_earned || 0}</div>
              <div className="text-sm text-slate-500">Months Earned</div>
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-slate-700">Your personal invite link</label>
            <div className="flex gap-3">
              <input
                type="text"
                value={referralLink || ''}
                readOnly
                className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none text-sm"
              />
              <button
                onClick={handleCopy}
                className="px-6 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </button>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-3">Or share directly:</p>
              <div className="flex gap-3">
                <button
                  onClick={shareOnTwitter}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#1DA1F2]/10 text-[#1DA1F2] rounded-xl hover:bg-[#1DA1F2]/20 transition-colors font-medium"
                >
                  <Twitter className="h-5 w-5" />
                  Twitter
                </button>
                <button
                  onClick={shareOnLinkedIn}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#0A66C2]/10 text-[#0A66C2] rounded-xl hover:bg-[#0A66C2]/20 transition-colors font-medium"
                >
                  <Linkedin className="h-5 w-5" />
                  LinkedIn
                </button>
                <button
                  onClick={shareViaEmail}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors font-medium"
                >
                  <Mail className="h-5 w-5" />
                  Email
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gold-50 to-amber-50 rounded-2xl p-8 border border-gold-200">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-gold-500" />
            How it works
          </h2>
          <div className="space-y-4">
            {[
              { step: 1, title: 'Share your link', desc: 'Send your personal invite link to friends who could use a thinking partner' },
              { step: 2, title: 'They sign up', desc: 'When they create an account using your link, they get 37 days free instead of 7' },
              { step: 3, title: 'You both win', desc: 'You get 30 extra days added to your trial too - no limit on referrals!' }
            ].map((item) => (
              <div key={item.step} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-gold-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/settings"
            className="text-slate-500 hover:text-slate-700 text-sm font-medium inline-flex items-center gap-1"
          >
            Back to Settings
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
